// ════════════════════════════════════════════════════════════════════════════
// M1.5 Madurez — formulario del cuestionario VALOR·IA (prompt 0728 F4.2)
// Carga el cuestionario vigente, agrupa preguntas por dimensión y captura las
// respuestas del usuario autenticado (multi-respondedor). Escala 0-4 / texto / sí-no.
// ════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback, type CSSProperties } from 'react';
import {
  fetchMadurezCuestionario,
  fetchMadurezRespuestas,
  guardarMadurezRespuestas,
  type MadurezCuestionario,
  type MadurezPregunta,
  type MadurezRespuestaInput,
  type DimensionMadurez,
} from '../../../../shared/services/api';
import { anclaOpcion } from '../descriptores-madurez';
import { BotonDictado } from '../../../../shared/components/BotonDictado';

const A1 = '#1F3864';
const A2 = '#2F5496';
const CE = '#33BCE1';
const GO = '#595959';
const GM = '#D9D9D9';
const W = '#FFF';

const DIM_ORDER: DimensionMadurez[] = [
  'FORMALIZACION',
  'GOBERNABILIDAD',
  'ALINEACION_ESTRATEGICA',
  'CAPACIDAD_CONTROL',
  'CAPACIDAD_EVOLUCION',
];

const miniBtn: CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  padding: '3px 8px',
  borderRadius: 5,
  border: `1px solid ${GM}`,
  background: W,
  color: A2,
  cursor: 'pointer',
  fontFamily: "'DM Sans',sans-serif",
};

interface Labels {
  intro: string;
  guardar: string;
  guardando: string;
  guardado: string;
  sinPreguntas: string;
  nota: string;
  lecturaSolo: string;
}

interface Props {
  evaluacionId: string;
  labels: Labels;
  dimLabel: (d: DimensionMadurez) => string;
  onSaved?: () => void;
  /** Diagnóstico APROBADA: cuestionario congelado, inputs deshabilitados. */
  readOnly?: boolean;
}

export function CuestionarioForm({ evaluacionId, labels, dimLabel, onSaved, readOnly }: Props) {
  const [cuestionario, setCuestionario] = useState<MadurezCuestionario | null>(null);
  const [valores, setValores] = useState<Record<string, number>>({});
  const [textos, setTextos] = useState<Record<string, string>>({});
  const [notas, setNotas] = useState<Record<string, string>>({});
  const [notaOpen, setNotaOpen] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Acordeón por dimensión: todas ABIERTAS por defecto (con todo desplegado se ve continuo).
  const [dimCerrada, setDimCerrada] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchMadurezCuestionario()
      .then((res) => setCuestionario(res.data))
      .catch((err: Error) => setError(err.message));
  }, []);

  // Recarga: prepobla el formulario con lo que el usuario YA respondió en esta
  // evaluación ("el cuestionario que ya cubrí"). Resetea al cambiar de evaluación.
  useEffect(() => {
    if (!evaluacionId) return;
    let cancelado = false;
    setValores({});
    setTextos({});
    setNotas({});
    setNotaOpen({});
    setSaved(false);
    setError(null);
    fetchMadurezRespuestas(evaluacionId)
      .then((res) => {
        if (cancelado) return;
        const nextValores: Record<string, number> = {};
        const nextTextos: Record<string, string> = {};
        const nextNotas: Record<string, string> = {};
        const nextOpen: Record<string, boolean> = {};
        for (const r of res.data) {
          if (r.valorTexto != null) nextTextos[r.preguntaId] = r.valorTexto;
          else if (r.valor != null) nextValores[r.preguntaId] = r.valor;
          if (r.nota != null) {
            nextNotas[r.preguntaId] = r.nota;
            nextOpen[r.preguntaId] = true;
          }
        }
        setValores(nextValores);
        setTextos(nextTextos);
        setNotas(nextNotas);
        setNotaOpen(nextOpen);
      })
      .catch((err: Error) => {
        if (!cancelado) setError(err.message);
      });
    return () => {
      cancelado = true;
    };
  }, [evaluacionId]);

  const guardar = useCallback(async () => {
    if (!cuestionario) return;
    const respuestas: MadurezRespuestaInput[] = [];
    for (const p of cuestionario.preguntas) {
      const nota = notas[p.id]?.trim() || undefined;
      if (p.tipoRespuesta === 'TEXTO_LIBRE') {
        const txt = textos[p.id]?.trim();
        if (txt || nota) respuestas.push({ preguntaId: p.id, valorTexto: txt ?? null, nota });
      } else if (valores[p.id] !== undefined || nota) {
        respuestas.push({ preguntaId: p.id, valor: valores[p.id] ?? null, nota });
      }
    }
    if (respuestas.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await guardarMadurezRespuestas(evaluacionId, respuestas);
      setSaved(true);
      onSaved?.();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }, [cuestionario, valores, textos, notas, evaluacionId, onSaved]);

  if (error && !cuestionario) {
    return <div style={{ color: '#7B0000', fontSize: 12, padding: 8 }}>{error}</div>;
  }
  if (!cuestionario) {
    return <div style={{ color: GO, fontSize: 12, padding: 8 }}>{labels.sinPreguntas}</div>;
  }

  const escala = [0, 1, 2, 3, 4];

  const renderPregunta = (p: MadurezPregunta) => (
    <div key={p.id} style={{ padding: '6px 0', borderBottom: `1px solid #F2F2F2` }}>
      <div style={{ fontSize: 12, color: '#000', marginBottom: 4 }}>{p.enunciado}</div>
      {p.tipoRespuesta === 'TEXTO_LIBRE' ? (
        <textarea
          value={textos[p.id] ?? ''}
          onChange={(e) => setTextos((prev) => ({ ...prev, [p.id]: e.target.value }))}
          rows={2}
          readOnly={readOnly}
          style={{
            width: '100%',
            fontSize: 12,
            fontFamily: "'DM Sans',sans-serif",
            border: `1px solid ${GM}`,
            borderRadius: 6,
            padding: 6,
            resize: 'vertical',
            background: readOnly ? '#F7F7F7' : W,
            color: readOnly ? GO : '#000',
          }}
        />
      ) : (
        <div style={{ display: 'flex', gap: 4 }}>
          {(p.tipoRespuesta === 'BOOLEAN' ? [0, 1] : escala).map((n) => {
            const sel = valores[p.id] === n;
            return (
              <button
                key={n}
                disabled={readOnly}
                title={p.tipoRespuesta === 'ESCALA_0_4' ? anclaOpcion(p.dimension, n) : undefined}
                onClick={() => !readOnly && setValores((prev) => ({ ...prev, [p.id]: n }))}
                style={{
                  width: 30,
                  height: 26,
                  borderRadius: 5,
                  border: sel ? `2px solid ${A1}` : `1px solid ${GM}`,
                  background: sel ? CE : W,
                  color: sel ? A1 : GO,
                  fontWeight: 700,
                  fontSize: 11,
                  cursor: readOnly ? 'default' : 'pointer',
                  opacity: readOnly && !sel ? 0.55 : 1,
                  fontFamily: "'DM Sans',sans-serif",
                }}
              >
                {p.tipoRespuesta === 'BOOLEAN' ? (n === 1 ? 'Sí' : 'No') : n}
              </button>
            );
          })}
        </div>
      )}
      {/* Ancla conductual del valor elegido (0738): da significado al número. */}
      {p.tipoRespuesta === 'ESCALA_0_4' && valores[p.id] !== undefined && (
        <div
          style={{ fontSize: 10, color: A2, marginTop: 3, fontStyle: 'italic', lineHeight: 1.35 }}
        >
          {valores[p.id]} — {anclaOpcion(p.dimension, valores[p.id])}
        </div>
      )}
      {!readOnly && (
        <button
          type="button"
          onClick={() => setNotaOpen((prev) => ({ ...prev, [p.id]: !prev[p.id] }))}
          style={{
            marginTop: 4,
            fontSize: 10,
            color: A2,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            fontFamily: "'DM Sans',sans-serif",
          }}
        >
          {(notaOpen[p.id] ? '− ' : '+ ') + labels.nota}
        </button>
      )}
      {notaOpen[p.id] && (
        <>
          <textarea
            value={notas[p.id] ?? ''}
            onChange={(e) => setNotas((prev) => ({ ...prev, [p.id]: e.target.value }))}
            maxLength={1000}
            rows={2}
            readOnly={readOnly}
            style={{
              width: '100%',
              marginTop: 4,
              fontSize: 12,
              fontFamily: "'DM Sans',sans-serif",
              border: `1px solid ${GM}`,
              borderRadius: 6,
              padding: 6,
              resize: 'vertical',
              background: readOnly ? '#F7F7F7' : W,
              color: readOnly ? GO : '#000',
            }}
          />
          {!readOnly && (
            <BotonDictado
              value={notas[p.id] ?? ''}
              onChange={(v) => setNotas((prev) => ({ ...prev, [p.id]: v }))}
            />
          )}
        </>
      )}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {readOnly && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: A1,
            background: '#EAF6FB',
            border: `1px solid ${CE}`,
            borderRadius: 6,
            padding: '6px 10px',
          }}
        >
          🔒 {labels.lecturaSolo}
        </div>
      )}
      <div style={{ fontSize: 11, color: GO }}>{labels.intro}</div>
      {/* Cabecera: contraer/expandir todo (bloques + notas cualitativas). */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" onClick={() => setDimCerrada({})} style={miniBtn}>
          ▾ Expandir bloques
        </button>
        <button
          type="button"
          onClick={() => setDimCerrada(Object.fromEntries(DIM_ORDER.map((d) => [d, true])))}
          style={miniBtn}
        >
          ▸ Contraer bloques
        </button>
        <button
          type="button"
          onClick={() =>
            setNotaOpen(Object.fromEntries(cuestionario.preguntas.map((p) => [p.id, true])))
          }
          style={miniBtn}
        >
          + Expandir notas
        </button>
        <button type="button" onClick={() => setNotaOpen({})} style={miniBtn}>
          − Contraer notas
        </button>
      </div>
      {DIM_ORDER.map((d) => {
        const preguntas = cuestionario.preguntas
          .filter((p) => p.dimension === d)
          .sort((a, b) => a.orden - b.orden);
        if (preguntas.length === 0) return null;
        const abierta = !dimCerrada[d];
        return (
          <div key={d}>
            <button
              type="button"
              onClick={() => setDimCerrada((prev) => ({ ...prev, [d]: !prev[d] }))}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: '#F5F8FC',
                border: `1px solid ${GM}`,
                borderRadius: 6,
                padding: '5px 8px',
                marginBottom: abierta ? 4 : 0,
                cursor: 'pointer',
                fontFamily: "'DM Sans',sans-serif",
                fontSize: 10,
                fontWeight: 800,
                color: A2,
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                textAlign: 'left',
              }}
            >
              <span style={{ fontSize: 11, color: A1, width: 10 }}>{abierta ? '▾' : '▸'}</span>
              <span style={{ flex: 1 }}>{dimLabel(d)}</span>
              <span style={{ color: GO, fontWeight: 600 }}>{preguntas.length}</span>
            </button>
            {abierta && preguntas.map(renderPregunta)}
          </div>
        );
      })}
      {error && <div style={{ color: '#7B0000', fontSize: 11 }}>{error}</div>}
      {!readOnly && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={guardar}
            disabled={saving}
            style={{
              fontSize: 12,
              fontWeight: 700,
              padding: '6px 14px',
              borderRadius: 6,
              border: 'none',
              background: A1,
              color: W,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.5 : 1,
              fontFamily: "'DM Sans',sans-serif",
            }}
          >
            {saving ? labels.guardando : labels.guardar}
          </button>
          {saved && (
            <span style={{ color: '#16A34A', fontSize: 12, fontWeight: 600 }}>
              {labels.guardado}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
